import { isEmpty } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import Button from "antd/lib/button";

import routeWithApiKeySession from "@/components/ApplicationArea/routeWithApiKeySession";
import BigMessage from "@/components/BigMessage";
// import PageHeader from "@/components/PageHeader";
import Parameters from "@/components/Parameters";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Filters from "@/components/Filters";
import VisualizationRenderer from "@/components/visualizations/VisualizationRenderer";
import { CrossCounterProvider } from "@/components/visualizations/CrossCounterContext";

import { Dashboard } from "@/services/dashboard";
import routes from "@/services/routes";
import location from "@/services/location";

import useDashboard from "./hooks/useDashboard";

import "./PublicDashboardPage.less";

function HiddenCountersSyncRenderers ({ widgets, filters, isPublic }) {
  const hiddenCounterWidgets = widgets.filter(widget => widget?.options?.showInPublic === false && widget?.visualization?.type === "COUNTER");

  return (
    <div style={{ display: "none" }}>
      {hiddenCounterWidgets.map(widget => {
        const queryResult = widget.getQueryResult ? widget.getQueryResult() : null;
        const queryStatus = queryResult && queryResult.getStatus ? queryResult.getStatus() : null;
        if (queryStatus !== "done") {
          return null;
        }

        return (
          <VisualizationRenderer
            key={`hidden-counter-${widget.id || widget.visualization?.id}`}
            visualization={widget.visualization}
            queryResult={queryResult}
            filters={filters}
            onFiltersChange={() => { }}
            context="widget"
            isPublic={isPublic}
            showFilters={false}
          />
        );
      })}
    </div>
  );
}

HiddenCountersSyncRenderers.propTypes = {
  widgets: PropTypes.arrayOf(PropTypes.object),
  filters: PropTypes.arrayOf(PropTypes.object),
  isPublic: PropTypes.bool,
};

HiddenCountersSyncRenderers.defaultProps = {
  widgets: [],
  filters: [],
  isPublic: false,
};

function PublicDashboard ({ dashboard }) {
  const { globalParameters, filters, setFilters, refreshDashboard, refreshing, loadWidget, refreshWidget } = useDashboard(dashboard, { isPublic: true });
  const hideParameters = location.search.hide_parameters;
  const publicWidgets = dashboard.widgets.filter(widget => widget.options.showInPublic !== false);
  const [isRefreshDocked, setIsRefreshDocked] = React.useState(false);
  const refreshingRef = React.useRef(refreshing);
  const prevRefreshingRef = React.useRef(refreshing);

  React.useEffect(() => {
    const dockTimer = setTimeout(() => {
      if (!refreshingRef.current) {
        setIsRefreshDocked(true);
      }
    }, 3000);

    return () => clearTimeout(dockTimer);
  }, []);

  React.useEffect(() => {
    if (!prevRefreshingRef.current && refreshing) {
      setIsRefreshDocked(false);
    }

    if (prevRefreshingRef.current && !refreshing) {
      setIsRefreshDocked(true);
    }

    prevRefreshingRef.current = refreshing;
    refreshingRef.current = refreshing;
  }, [refreshing]);

  return (
    <CrossCounterProvider refreshing={refreshing}>
      <HiddenCountersSyncRenderers widgets={dashboard.widgets} filters={filters} isPublic />
      <div className={`public-dashboard-floating-refresh ${isRefreshDocked ? "is-docked" : ""}`}>
        <Button
          className={`public-dashboard-refresh-btn ${isRefreshDocked ? "is-docked" : ""}`}
          type="primary"
          onClick={() => {
            setIsRefreshDocked(false);
            refreshDashboard();
          }}
          loading={refreshing}
          disabled={refreshing}
          aria-label="刷新查询数据"
          title={isRefreshDocked ? "展开刷新按钮" : "刷新查询数据"}>
          {!refreshing && <i className="zmdi zmdi-refresh public-dashboard-refresh-icon" aria-hidden="true" />}
          <span className="public-dashboard-refresh-label">刷新查询数据</span>
          {isRefreshDocked && !refreshing && <i className="zmdi zmdi-chevron-left public-dashboard-expand-icon" aria-hidden="true" />}
        </Button>
      </div>
      <div className="container p-t-10 p-b-10">
        {/*<PageHeader title={dashboard.name} /> */}
        {!isEmpty(globalParameters) && !hideParameters && (
          <div className="m-l-15 m-r-15 m-b-15 p-15 bg-white tiled">
            <Parameters parameters={globalParameters} onValuesChange={refreshDashboard} autoApply />
          </div>
        )}
        {!isEmpty(filters) && !hideParameters && (
          <div className="m-b-10 p-15 bg-white tiled">
            <Filters filters={filters} onChange={setFilters} />
          </div>
        )}
        <div id="dashboard-container">
          <DashboardGrid
            dashboard={dashboard}
            widgets={publicWidgets}
            filters={filters}
            isEditing={false}
            isPublic
            onLoadWidget={loadWidget}
            onRefreshWidget={refreshWidget}
          />
        </div>
      </div>
    </CrossCounterProvider>
  );
}

PublicDashboard.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

class PublicDashboardPage extends React.Component {
  static propTypes = {
    token: PropTypes.string.isRequired,
    onError: PropTypes.func,
  };

  static defaultProps = {
    onError: () => {},
  };

  state = {
    loading: true,
    dashboard: null,
  };

  componentDidMount() {
    Dashboard.getByToken({ token: this.props.token })
      .then(dashboard => this.setState({ dashboard, loading: false }))
      .catch(error => this.props.onError(error));
  }

  render() {
    const { loading, dashboard } = this.state;
    return (
      <div className="public-dashboard-page">
        {loading ? (
          <div className="container loading-message">
            <BigMessage className="" icon="fa-spinner fa-2x fa-pulse" message="Loading..." />
          </div>
        ) : (
          <PublicDashboard dashboard={dashboard} />
        )}
      </div>
    );
  }
}

routes.register(
  "Dashboards.ViewShared",
  routeWithApiKeySession({
    path: "/public/dashboards/:token",
    render: pageProps => <PublicDashboardPage {...pageProps} />,
    getApiKey: currentRoute => currentRoute.routeParams.token,
  })
);
